import { useMemo } from "react"
import { Check, PlusCircle } from "lucide-react"
import { Badge } from "@components/ui/badge"
import { Button } from "@components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover"
import { Separator } from "@components/ui/separator"
import { cn } from "@components/ui/lib/utils"
import type { MultiSelectFilterProps } from "./types"
import { strictFilter as defaultStrictFilter, sortOptions } from "./utils"

export function MultiSelectFilter(props: MultiSelectFilterProps) {
  const {
    values = [],
    onChange,
    options,
    placeholder,
    className,
    sortSelectedFirst = true,
    customFilter,
    facets,
  } = props

  // Determine filter function to use
  const filterFn = customFilter || defaultStrictFilter

  // Get sorted options
  const sortedOptions = useMemo(() => {
    if (!sortSelectedFirst) return options
    const selectedSet = new Set(values)
    return sortOptions(options, selectedSet)
  }, [options, sortSelectedFirst, values])

  const selectedValues = new Set(values)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "tw:flex tw:w-full tw:justify-start tw:gap-2 tw:border-border",
            className,
          )}
        >
          <PlusCircle />
          {placeholder || "Select..."}
          {selectedValues.size > 0 && (
            <>
              <Separator orientation="vertical" />
              <Badge
                variant="secondary"
                className="tw:rounded-sm tw:px-1 tw:font-normal tw:md:hidden"
              >
                {selectedValues.size}
              </Badge>
              <div className="tw:hidden tw:gap-1 tw:md:flex">
                {selectedValues.size > 2 ? (
                  <Badge
                    variant="secondary"
                    className="tw:rounded-sm tw:px-1 tw:font-normal"
                  >
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  sortedOptions
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge
                        variant="secondary"
                        key={String(option.value)}
                        className="tw:rounded-sm tw:px-1 tw:font-normal"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="tw:w-fit tw:p-0" align="start">
        <Command filter={filterFn}>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onChange(undefined)}
                    className="tw:cursor-pointer tw:justify-center tw:text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
            <CommandGroup>
              {sortedOptions.map((option) => {
                const isSelected = selectedValues.has(option.value)
                return (
                  <CommandItem
                    key={String(option.value)}
                    value={option.label}
                    onSelect={() => {
                      if (isSelected) {
                        selectedValues.delete(option.value)
                      } else {
                        selectedValues.add(option.value)
                      }
                      const filterValues = Array.from(selectedValues)
                      onChange(filterValues.length ? filterValues : undefined)
                    }}
                    className="tw:cursor-pointer"
                  >
                    <div
                      className={cn(
                        "tw:mr-2 tw:flex tw:size-4 tw:items-center tw:justify-center tw:rounded-sm tw:border tw:border-primary",
                        isSelected
                          ? "tw:bg-primary"
                          : "tw:opacity-50 tw:[&_svg]:invisible",
                      )}
                    >
                      <Check className="tw:size-4 tw:text-primary-foreground" />
                    </div>
                    {option.icon && (
                      <option.icon className="tw:mr-2 tw:size-4 tw:text-muted-foreground" />
                    )}
                    <span>{option.label}</span>
                    {facets?.get(option.value) && (
                      <span className="tw:ml-auto tw:flex tw:size-4 tw:items-center tw:justify-center tw:text-xs">
                        {facets.get(option.value)}
                      </span>
                    )}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
